import React, { useEffect, useRef, memo } from 'react'
import PropTypes from 'prop-types'
import { useRecoilValue } from 'recoil'
import { introData as introDataState } from '../../store/dataStore'
import { useAnimation } from 'framer-motion'
import { LogoIconWrapper, LogoTextWrapper } from '../../styles/globalStyles'
import {
  IntroSection,
  LogoWrapper,
  Tagline,
} from '../../styles/introStyles'

import LogoIcon from '../../assets/svg/LogoIcon'
import LogoText from '../../assets/svg/LogoText'
import ScrollDownButton from '../Navigation/ScrollDownButton'

const Intro = ({ visible }) => {
  const introData = useRecoilValue(introDataState)
  const introSection = useRef(null)
  const logoIcon = useRef(null)
  const logoText = useRef(null)
  const tagline = useRef(null)
  const nextButton = useRef(null)

  const introSectionVars = {
    hidden: {
      visibility: 'hidden',
      zIndex: -1,
      opacity: 0,
    },
    visible: {
      visibility: 'visible',
      zIndex: 1,
      opacity: 1,
    },
    exit: {
      opacity: 0,
      transition: {
        duration: 0.5,
      },
      transitionEnd: {
        visibility: 'hidden',
        zIndex: -1,
      },
    },
  }

  const logoIconVars = {
    hidden: {
      opacity: 0,
      rotate: -270,
    },
    visible: {
      opacity: 1,
      rotate: [-270, 0],
    },
  }

  const logoTextVars = {
    hidden: {
      opacity: 0,
      x: '120%',
    },
    visible: {
      opacity: 1,
      x: 0,
    },
  }

  const taglineVars = {
    hidden: {
      opacity: 0,
    },
    visible: {
      opacity: 1,
    },
  }

  const sectionAnims = useAnimation(introSectionVars)
  const logoIconAnims = useAnimation(logoIconVars)
  const logoTextAnims = useAnimation(logoTextVars)
  const taglineAnims = useAnimation(taglineVars)

  useEffect(() => {
    if (visible) {
      sectionAnims.start('visible')
      logoIconAnims.start('visible')
      logoTextAnims.start('visible')
      taglineAnims.start('visible')
      nextButton.current.play()
    } else {
      sectionAnims.start('exit')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible])

  return (
    <IntroSection
      ref={introSection}
      initial='hidden'
      animate={sectionAnims}
      variants={introSectionVars}
      transition={{
        opacity: {
          duration: 0.5,
          ease: 'linear',
        },
        default: {
          duration: 0,
          ease: 'none',
        },
      }}>
      <LogoWrapper>
        <LogoIconWrapper
          ref={logoIcon}
          initial='hidden'
          animate={logoIconAnims}
          variants={logoIconVars}
          transition={{
            duration: 2.5,
            ease: 'easeOut',
          }}>
          <LogoIcon />
        </LogoIconWrapper>
        <LogoTextWrapper
          ref={logoText}
          initial='hidden'
          animate={logoTextAnims}
          variants={logoTextVars}
          style={{
            y: '-50%',
          }}
          transition={{
            duration: 2,
            ease: 'easeOut',
          }}>
          <LogoText />
        </LogoTextWrapper>
      </LogoWrapper>
      <Tagline
        ref={tagline}
        initial='hidden'
        animate={taglineAnims}
        variants={taglineVars}
        transition={{
          duration: 1,
          delay: 2.75,
          ease: 'easeOut',
        }}>
        {introData}
      </Tagline>
      <ScrollDownButton ref={nextButton} />
    </IntroSection>
  )
}

Intro.propTypes = {
  visible: PropTypes.bool.isRequired,
}

export default memo(Intro)
